import React, { forwardRef } from 'react';
import { cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';
import { contextFactory } from '../../../hooks';
import { PolymorphicComponentPropWithRef, PolymorphicRef } from '../../../type-utils';
import * as styles from '../accordion.styles';
import { useAccordionContext } from '../provider/accordion.provider';

interface AccordionItemContext {
  value: string;
  hasMultiLocations?: boolean;
  trackingId?: string;
}

export const [AccordionItemContext, useAccordionItemContext] = contextFactory<AccordionItemContext>();

type Props = {
  value: string;
  hasMultiLocations?: boolean;
  trackingId?: string;
};

type AccordionItemProps<C extends React.ElementType> = PolymorphicComponentPropWithRef<C, Props>;

type AccordionItemComponent = <C extends React.ElementType = 'div'>(props: AccordionItemProps<C>) => React.ReactNode;

const MARGIN_VARIANTS = ['default', 'error', 'location', 'hybrid', 'main'] as const;
const SHADOW_OPT_IN_VARIANTS = ['error', 'location', 'hybrid', 'main'] as const;

export const AccordionItem: AccordionItemComponent = forwardRef(
  <C extends React.ElementType = 'div'>(
    { children, className, as, value, hasMultiLocations, trackingId, ...others }: AccordionItemProps<C>,
    ref: PolymorphicRef<C>
  ) => {
    const { isItemActive, variant = 'default', distance, showBoxShadow } = useAccordionContext();

    const hasMargin = (MARGIN_VARIANTS as readonly string[]).includes(variant);
    const showShadow =
      variant === 'default'
        ? showBoxShadow !== false
        : (SHADOW_OPT_IN_VARIANTS as readonly string[]).includes(variant) && !!showBoxShadow;

    const itemClass = cx(
      styles.itemBaseStyle,
      styles.itemVariantStyles[variant],
      showShadow && styles.itemBoxShadowStyle,
      className
    );

    const inlineStyle = hasMargin
      ? { marginBottom: theme.spacing(distance ?? (variant === 'default' ? 1.5 : 2)) }
      : undefined;

    const Component = as || 'div';

    return (
      <AccordionItemContext.Provider value={{ value, hasMultiLocations, trackingId }}>
        <Component
          className={itemClass}
          style={inlineStyle}
          ref={ref}
          data-active={isItemActive(value) || undefined}
          {...others}
        >
          {children}
        </Component>
      </AccordionItemContext.Provider>
    );
  }
);
